import React from 'react';

export default function PrivacyPolicy() {
  return (
    <main className="min-h-screen bg-neutral-50 text-neutral-900 font-sans">
      {/* Hero / Banner */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-[#1b0d0b] to-neutral-700 opacity-95" />
        <svg aria-hidden="true" className="absolute -top-12 -left-12 h-[38rem] w-[38rem] opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="dots-pp" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots-pp)" />
        </svg>
        <div className="relative mx-auto w-full max-w-5xl px-4 py-16 md:py-24 text-center text-white">
          <div className="mx-auto inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-4 py-1 backdrop-blur">
            <span className="text-[11px] tracking-[0.25em]">LEGAL</span>
          </div>
          <h1 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight">Privacy Policy</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm md:text-base text-white/80">
            Your privacy matters to us. Learn how we collect, use, and protect your personal information.
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="mx-auto w-full max-w-5xl px-4 py-10 md:py-14">
        {/* Quick summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SummaryCard
            title="Data Protection"
            text="Your personal data is stored securely and never sold to third parties."
            icon={(
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1.5a.75.75 0 0 1 .493.186l7.5 6.75A.75.75 0 0 1 20.25 9v9A2.25 2.25 0 0 1 18 20.25H6A2.25 2.25 0 0 1 3.75 18V9a.75.75 0 0 1 .257-.564l7.5-6.75A.75.75 0 0 1 12 1.5Zm0 2.189L5.25 9.384V18c0 .414.336.75.75.75h12a.75.75 0 0 0 .75-.75V9.384L12 3.689Zm0 4.061a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z" />
              </svg>
            )}
          />
          <SummaryCard
            title="No Third-Party Sharing"
            text="We do not sell, trade, or share your information with outside parties."
            icon={(
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75ZM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-.75a1.875 1.875 0 0 1-1.875-1.875V8.625ZM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v6.75c0 1.035-.84 1.875-1.875 1.875h-.75A1.875 1.875 0 0 1 3 19.875v-6.75Z" />
              </svg>
            )}
          />
          <SummaryCard
            title="Your Rights"
            text="You can access, update, or request deletion of your personal data at any time."
            icon={(
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.25a9.75 9.75 0 1 0 9.75 9.75A9.761 9.761 0 0 0 12 2.25Zm0 4.5a2.25 2.25 0 1 1-2.25 2.25A2.25 2.25 0 0 1 12 6.75Zm0 11.25a7.5 7.5 0 0 1-6-3.03 6 6 0 0 1 12 0 7.5 7.5 0 0 1-6 3.03Z" />
              </svg>
            )}
          />
        </div>

        {/* Detailed sections */}
        <div className="mt-10 space-y-8">
          <DetailSection
            number="1"
            title="Introduction"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4.5 6.75A2.25 2.25 0 0 1 6.75 4.5h10.5A2.25 2.25 0 0 1 19.5 6.75v10.5A2.25 2.25 0 0 1 17.25 19.5H6.75A2.25 2.25 0 0 1 4.5 17.25V6.75Zm3 2.25h9a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1 0-1.5Zm0 3h9a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1 0-1.5Z" />
              </svg>
            )}
          >
            At <strong>Emba by Emefa</strong>, we are committed to safeguarding your privacy. This Privacy Policy explains how we collect, use, and protect your personal information when you use our website and services. By using our services, you consent to the practices described in this policy.
          </DetailSection>

          <DetailSection
            number="2"
            title="Information We Collect"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.375 3C2.339 3 1.5 3.84 1.5 4.875v.75c0 1.036.84 1.875 1.875 1.875h17.25c1.035 0 1.875-.84 1.875-1.875v-.75C22.5 3.839 21.66 3 20.625 3H3.375ZM3 9.375C3 8.339 3.84 7.5 4.875 7.5h14.25C20.16 7.5 21 8.34 21 9.375v1.5C21 11.161 20.16 12 19.125 12H4.875C3.839 12 3 11.16 3 10.125v-1.5Zm0 5.625C3 13.964 3.84 13.5 4.875 13.5h14.25c1.035 0 1.875.464 1.875 1.5v3c0 1.035-.84 1.875-1.875 1.875H4.875A1.875 1.875 0 0 1 3 18.625v-3Z" />
              </svg>
            )}
          >
            We collect the following types of information to provide and improve our services:
            <ul className="mt-2 list-disc list-inside space-y-1">
              <li><strong>Personal Identification Information:</strong> Name, email address, and phone number provided during account registration or booking.</li>
              <li><strong>Booking Details:</strong> Appointment dates, attire preferences, and related service information.</li>
              <li><strong>Technical Data:</strong> Browser type, device information, and IP address collected automatically when you visit our site.</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on our platform, and interactions with site features.</li>
            </ul>
          </DetailSection>

          <DetailSection
            number="3"
            title="How We Use Your Information"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 .75a8.25 8.25 0 0 0-4.135 15.39c.686.398 1.115 1.008 1.134 1.623a.75.75 0 0 0 .577.706c.352.083.71.148 1.074.195.323.041.6-.218.6-.544v-4.661a6.714 6.714 0 0 1-.937-.171.75.75 0 1 1 .374-1.453 5.261 5.261 0 0 0 2.626 0 .75.75 0 1 1 .374 1.452 6.712 6.712 0 0 1-.937.172v4.661c0 .326.277.585.6.544.364-.047.722-.112 1.074-.195a.75.75 0 0 0 .577-.706c.02-.615.448-1.225 1.134-1.623A8.25 8.25 0 0 0 12 .75Z" />
              </svg>
            )}
          >
            The personal information we collect is used solely to:
            <ul className="mt-2 list-disc list-inside space-y-1">
              <li>Process and manage your bookings and appointments.</li>
              <li>Communicate with you regarding your orders or inquiries.</li>
              <li>Send service-related notifications and updates.</li>
              <li>Improve our website, services, and customer experience.</li>
              <li>Comply with legal obligations.</li>
            </ul>
            We will never use your data for unsolicited marketing without your explicit consent.
          </DetailSection>

          <DetailSection
            number="4"
            title="No Third-Party Sharing"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75" />
              </svg>
            )}
          >
            We do <strong>not</strong> sell, rent, trade, or otherwise share your personal information with any third parties for their commercial purposes. Your data is strictly used within <strong>Emba by Emefa</strong> to deliver and improve our services. We may only disclose your information when required by law or to protect our legal rights.
          </DetailSection>

          <DetailSection
            number="5"
            title="Data Security"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1.5a.75.75 0 0 1 .493.186l7.5 6.75A.75.75 0 0 1 20.25 9v9A2.25 2.25 0 0 1 18 20.25H6A2.25 2.25 0 0 1 3.75 18V9a.75.75 0 0 1 .257-.564l7.5-6.75A.75.75 0 0 1 12 1.5Zm0 2.189L5.25 9.384V18c0 .414.336.75.75.75h12a.75.75 0 0 0 .75-.75V9.384L12 3.689Z" />
              </svg>
            )}
          >
            We implement industry-standard technical and organisational security measures to protect your personal information against unauthorised access, loss, or misuse. Access to your personal data is restricted to authorised personnel only. While we strive to protect your data, no method of transmission over the internet is 100% secure, and we encourage you to use strong passwords and keep your login credentials confidential.
          </DetailSection>

          <DetailSection
            number="6"
            title="Cookies & Tracking"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 9a.75.75 0 0 0-1.5 0v2.25H9a.75.75 0 0 0 0 1.5h2.25V15a.75.75 0 0 0 1.5 0v-2.25H15a.75.75 0 0 0 0-1.5h-2.25V9Z" />
              </svg>
            )}
          >
            Our website may use cookies and similar technologies to enhance your browsing experience and analyse website traffic. Cookies are small text files stored on your device. You may choose to disable cookies via your browser settings; however, doing so may affect the functionality of certain features on our site. We do not use cookies to track you across other websites.
          </DetailSection>

          <DetailSection
            number="7"
            title="Data Retention"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.75 3a.75.75 0 0 0-.75.75V6h11.25V3.75a.75.75 0 0 0-1.5 0V4.5h-7.5V3.75a.75.75 0 0 0-.75-.75ZM4.5 7.5h15a.75.75 0 0 1 .75.75v9A2.25 2.25 0 0 1 18 19.5H6a2.25 2.25 0 0 1-2.25-2.25v-9a.75.75 0 0 1 .75-.75Z" />
              </svg>
            )}
          >
            We retain your personal information only for as long as necessary to fulfil the purposes described in this policy, or as required by applicable law. When your data is no longer needed, we will securely delete or anonymise it. You may request deletion of your account and associated data at any time by contacting us.
          </DetailSection>

          <DetailSection
            number="8"
            title="Your Rights"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.25a4.5 4.5 0 0 1 4.5 4.5V9h1.5A2.25 2.25 0 0 1 20.25 11.25v7.5A2.25 2.25 0 0 1 18 21H6a2.25 2.25 0 0 1-2.25-2.25v-7.5A2.25 2.25 0 0 1 5.25 9H6V6.75A4.5 4.5 0 0 1 10.5 2.25h3ZM7.5 9h9V6.75a3 3 0 1 0-6 0V9Z" />
              </svg>
            )}
          >
            You have the following rights regarding your personal data:
            <ul className="mt-2 list-disc list-inside space-y-1">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Ask us to correct inaccurate or incomplete data.</li>
              <li><strong>Deletion:</strong> Request that we delete your personal data from our systems.</li>
              <li><strong>Objection:</strong> Object to the processing of your data in certain circumstances.</li>
            </ul>
            To exercise any of these rights, please contact us using the details below.
          </DetailSection>

          <DetailSection
            number="9"
            title="Children's Privacy"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.25 4.533A9.707 9.707 0 0 0 6 3a9.735 9.735 0 0 0-3.25.555.75.75 0 0 0-.5.707v14.25a.75.75 0 0 0 1 .707A8.237 8.237 0 0 1 6 18.75c1.995 0 3.823.707 5.25 1.886V4.533ZM12.75 20.636A8.214 8.214 0 0 1 18 18.75c.966 0 1.89.166 2.75.47a.75.75 0 0 0 1-.708V4.262a.75.75 0 0 0-.5-.707A9.735 9.735 0 0 0 18 3a9.707 9.707 0 0 0-5.25 1.533v16.103Z" />
              </svg>
            )}
          >
            Our services are not directed to individuals under the age of 12. We do not knowingly collect personal information from minors. If you believe that a child has provided us with personal data, please contact us immediately and we will take steps to remove that information from our systems.
          </DetailSection>

          <DetailSection
            number="10"
            title="Changes to This Policy"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32l8.4-8.4Z" />
              </svg>
            )}
          >
            We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. Updated versions will be posted on this page with a revised "Last Updated" date. We encourage you to review this page periodically to stay informed about how we protect your information.
          </DetailSection>

          <DetailSection
            number="11"
            title="Contact Us"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.25 6.75A2.25 2.25 0 0 1 4.5 4.5h15a2.25 2.25 0 0 1 2.25 2.25v10.5A2.25 2.25 0 0 1 19.5 19.5H4.5A2.25 2.25 0 0 1 2.25 17.25V6.75Zm2.4.6a.75.75 0 0 0-.6 1.38l7.2 3.9a.75.75 0 0 0 .72 0l7.2-3.9a.75.75 0 0 0-.6-1.38L12 10.32 4.65 7.35Z" />
              </svg>
            )}
          >
            If you have any questions, concerns, or requests regarding this Privacy Policy or the handling of your personal data, please contact us at{' '}
            <a href="mailto:emefabansa@gmail.com" className="text-neutral-900 underline decoration-1 underline-offset-2 hover:no-underline">
              emefabansa@gmail.com
            </a>
            {' '}or call us at <strong>+233 542661704</strong>.
          </DetailSection>
        </div>

        <footer className="mt-14 text-center text-xs text-neutral-500">
          Last Updated: May 2026
        </footer>
      </div>
    </main>
  );
}

function SummaryCard({ title, text, icon }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm transition hover:shadow-md">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-900 text-white">
          {icon}
        </div>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <p className="mt-2 text-[13px] text-neutral-600">{text}</p>
    </div>
  );
}

function DetailSection({ number, title, icon, children }) {
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5 md:p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white text-xs font-semibold">
          {number}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="text-neutral-900">{icon}</div>
            <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
          </div>
          <div className="mt-2 text-sm leading-relaxed text-neutral-700">{children}</div>
        </div>
      </div>
    </section>
  );
}

